import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import type { OrderLocation } from "./InfoPopup";
import type { OptimizedDriverRoute } from "./getOptimizedRoute";
import { defaultEndpoint, DriverAssignment, DriverRouteDraft, EndpointDraft, previewAssignments, removeEndpointOrder, RouteMetrics } from "./route-preview";
import RoutePreviewMap from "./RoutePreviewMap";
import PreChallanExportButton from "./PreChallanExportButton";
import { summarizePolygonOrders, PolygonSelectionSummary } from "./polygon-selection";
import "./route-preview.css";
import EndpointEditor from "./RouteEndpointEditor";

export type RouteReviewContext = { orders: OrderLocation[]; assignments: DriverAssignment[]; replacingRouteIndex?: number;
  optimize: (orders: OrderLocation[], drivers: DriverAssignment[]) => Promise<OptimizedDriverRoute[]> };
type Props = RouteReviewContext & { onClose: (routes: OptimizedDriverRoute[] | null) => void; embedded?: boolean;
  onMetrics?: (metrics: RouteMetrics[]) => void; onSelection?: (summary: PolygonSelectionSummary) => void;
  map?: google.maps.Map | null; onRoutedOrderIds?: (ids: string[]) => void;
  onLoadingChange?: (loading: boolean) => void; onActiveOrderIds?: (ids: string[]) => void;
  addOrderRequest?: { orderId: string; token: number } | null };

export default function RoutePreviewDialog({ orders, assignments, optimize, onClose, embedded = false, onMetrics, onSelection, map, onRoutedOrderIds,
  onLoadingChange, onActiveOrderIds, addOrderRequest }: Props) {
  const drivers = assignments.map(a => a.driver);
  const [removed, setRemoved] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<DriverRouteDraft[]>(() => assignments.map(a => ({ ...a, start: defaultEndpoint(), end: defaultEndpoint() })));
  const [executive, setExecutive] = useState(drivers[0]?.driverId);
  const vehicleNumbers = Object.fromEntries(assignments.map(a => [a.driver.driverId, a.vehicleNo]));
  const [routes, setRoutes] = useState<OptimizedDriverRoute[]>([]);
  const reversedRoutes = useRef<OptimizedDriverRoute[] | null>(null);
  useEffect(() => { onRoutedOrderIds?.(routes.flatMap(r => r.stops.map(o => String(o.orderId)))); }, [routes, onRoutedOrderIds]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [mapError, setMapError] = useState("");
  const [mapReady, setMapReady] = useState(false);
  const [roadMetrics, setRoadMetrics] = useState<RouteMetrics[]>([]);
  useEffect(() => { onMetrics?.(mapReady ? roadMetrics : []); }, [onMetrics, mapReady, roadMetrics]);
  const [retry, setRetry] = useState(0);
  const finished = useRef(false);
  const activeOrders = useMemo(() => orders.filter(o => !removed.includes(String(o.orderId))), [orders, removed]);
  const routedIds = routes.flatMap(route => route.stops.map(order => String(order.orderId)));
  const routesMatch = routedIds.length === activeOrders.length && new Set(routedIds).size === routedIds.length &&
    activeOrders.every(order => routedIds.includes(String(order.orderId)));
  useEffect(() => { if (addOrderRequest) setRemoved(prev => prev.filter(id => id !== addOrderRequest.orderId)); }, [addOrderRequest]);
  useEffect(() => { onActiveOrderIds?.(activeOrders.map(order => String(order.orderId))); }, [activeOrders, onActiveOrderIds]);
  useEffect(() => { onLoadingChange?.(!error && !mapError && (busy || !mapReady || !routesMatch)); }, [busy, mapReady, routesMatch, error, mapError, onLoadingChange]);
  useEffect(() => { onSelection?.(summarizePolygonOrders(activeOrders)); }, [activeOrders, onSelection]);
  const prepared = useMemo(() => {
    try { return { assignments: previewAssignments(drafts, activeOrders), error: "" }; }
    catch (e) { return { assignments: [], error: e instanceof Error ? e.message : "Check start and end points." }; }
  }, [drafts, activeOrders]);

  useEffect(() => {
    let cancelled = false;
    setRoutes([]); setMapReady(false); setMapError("");
    setError(prepared.error); setBusy(!prepared.error);
    if (prepared.error) return;
    if (reversedRoutes.current) { setRoutes(reversedRoutes.current); reversedRoutes.current = null; setBusy(false); return; }
    const timer = window.setTimeout(() => {
      void optimize(activeOrders, prepared.assignments).then(result => {
        if (!cancelled) { setRoutes(result); setBusy(false); }
      }).catch(e => { if (!cancelled) { setError(e instanceof Error ? e.message : "Route optimization failed. Please retry."); setBusy(false); } });
    }, 450);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [activeOrders, prepared, optimize, retry]);

  const current = drafts.find(d => d.driver.driverId === executive)!;
  const hasVehicles = drivers.every(d => !!vehicleNumbers[d.driverId]);
  const duplicateVehicle = new Set(Object.values(vehicleNumbers).filter(Boolean)).size !== Object.values(vehicleNumbers).filter(Boolean).length;
  const canApply = !busy && !error && !mapError && mapReady && routesMatch && routes.length > 0 && hasVehicles && !duplicateVehicle;
  const updateEndpoint = (end: "start" | "end", value: EndpointDraft) => {
    reversedRoutes.current = null;
    setRoutes([]); setMapReady(false);
    setDrafts(prev => prev.map(d => d.driver.driverId === executive ? { ...d, [end]: value } : d));
  };
  const setOrderEndpoint = useCallback((driverId: number, orderId: string, end: "start" | "end") => {
    reversedRoutes.current = null; setRoutes([]); setMapReady(false); setExecutive(driverId);
    setDrafts(previous => previous.map(draft => draft.driver.driverId === driverId
      ? { ...draft, [end]: { mode: "order", orderId, coordinates: "" } } : draft));
  }, []);
  const removeOrder = (orderId: string) => {
    if (activeOrders.length <= 1 || !activeOrders.some(order => String(order.orderId) === orderId)) return;
    reversedRoutes.current = null;
    setRoutes([]); setMapReady(false); setRemoved(prev => [...prev, orderId]);
    setDrafts(prev => removeEndpointOrder(prev, orderId));
  };
  const reverseRoute = (driverId: number) => {
    if (!canApply) return;
    const route = routes.find(item => item.driverId === driverId);
    if (!route?.start || !route.end) return;
    const reversed = { ...route, start: route.end, end: route.start,
      stops: [...route.stops].sort((a,b) => (b.sequence ?? 0) - (a.sequence ?? 0)).map((order, index) => ({ ...order, sequence: index + 1 })) };
    reversedRoutes.current = routes.map(item => item === route ? reversed : item);
    setMapReady(false); setRoutes([]);
    setDrafts(previous => previous.map(draft => draft.driver.driverId === driverId ? { ...draft,
      start: { mode: "coordinates", coordinates: `${reversed.start.lat},${reversed.start.lng}`, orderId: "" },
      end: { mode: "coordinates", coordinates: `${reversed.end.lat},${reversed.end.lng}`, orderId: "" } } : draft));
  };
  const close = (result: OptimizedDriverRoute[] | null) => { if (!finished.current) { finished.current = true; onClose(result); } };
  const included = new Map(routes.flatMap(r => r.stops.map(o => [String(o.orderId), { driver: r.driverName, sequence: o.sequence }] as const)));

  const footer = <div className="flex justify-content-end gap-2">
      <Button label="Cancel" outlined onClick={() => close(null)} />
      <Button label="Apply route" icon="pi pi-check" disabled={!canApply} onClick={() => {
        if (canApply) close(routes.map(r => ({ ...r, vehicleNo: vehicleNumbers[r.driverId],
          totalDistanceKm: roadMetrics.find(m => m.driverId === r.driverId)?.distanceKm ?? r.totalDistanceKm,
          drivingMinutes: roadMetrics.find(m => m.driverId === r.driverId)?.drivingMinutes })));
      }} />
    </div>;
  const controls = <div className="route-preview-toolbar">
      <div><label htmlFor="preview-executive">Delivery executive</label><Dropdown inputId="preview-executive" value={executive}
        options={drivers.map(d => ({ label: `${d.name} — ${d.phonenumber} — ${d.posName || d.posCode || "POS unavailable"}`, value: d.driverId }))}
        onChange={e => setExecutive(e.value)} /></div>
      <div><label htmlFor="preview-vehicle">Vehicle for {current.driver.name}</label><InputText id="preview-vehicle"
        value={vehicleNumbers[executive!] || ""} readOnly /></div>
      <EndpointEditor label={`Start for ${current.driver.name}`} value={current.start} fallback={current.driver.startLocation} orders={activeOrders}
        onChange={value => updateEndpoint("start", value)} />
      <EndpointEditor label={`End for ${current.driver.name}`} value={current.end} fallback={current.driver.endLocation} orders={activeOrders}
        onChange={value => updateEndpoint("end", value)} />
    </div>;
  const content = <>
    {!embedded && controls}
    {busy && <p role="status">Recalculating delivery routes…</p>}
    {!busy && routes.length > 0 && !mapReady && !mapError && <p role="status">Drawing the road routes…</p>}
    {(error || mapError || duplicateVehicle || !hasVehicles) && <p role="alert" className="text-red-600">
      {error || mapError || "Close the preview and choose a different vehicle for each executive."}</p>}
    <div className="route-preview-content">
      <RoutePreviewMap orders={activeOrders} routes={routes} map={map} onReady={setMapReady} onError={setMapError} onMetrics={setRoadMetrics}
        onSetEndpoint={setOrderEndpoint} onReverseRoute={reverseRoute} onRemoveFromRoute={removeOrder} />
      <div className="route-preview-orders">
        {embedded && <details className="route-preview-endpoints"><summary>Start / end points</summary>{controls}
          <small>Use saved coordinates, an included order, or enter latitude and longitude. Removing an endpoint order restores its saved default.</small></details>}
        <div className="route-preview-summary">
        <div className="route-preview-summary-heading"><strong>{activeOrders.length} included orders</strong>
          <Button label="Recalculate" icon="pi pi-refresh" size="small" outlined disabled={busy} onClick={() => setRetry(n => n + 1)} /></div>
        <p>Total value (before GST): ₹{activeOrders.reduce((sum, o) => sum + Number(o.taxableAmount ?? o.amount ?? 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        <PreChallanExportButton orderIds={activeOrders.map(o => String(o.orderId))} />
        <Button label="Reverse route" icon="pi pi-sort-alt" outlined size="small" title={`Reverse ${current.driver.name}'s route`}
          disabled={!canApply || !routes.some(route => route.driverId === executive)} onClick={() => reverseRoute(executive!)} />
        </div>
        {activeOrders.map(o => <div className="route-preview-order" key={o.orderId}>
          <div className="route-preview-order-info"><strong>{o.orderId}</strong><span className="route-preview-customer">{o.customerName}</span><small>{included.get(String(o.orderId))?.driver || "Awaiting route"}
            {included.has(String(o.orderId)) ? ` · Stop ${included.get(String(o.orderId))?.sequence}` : ""}</small></div>
          <Button icon="pi pi-times" outlined severity="danger" size="small" aria-label={`Remove order ${o.orderId}`}
            disabled={activeOrders.length <= 1} onClick={() => removeOrder(String(o.orderId))} />
        </div>)}
        {removed.length > 0 && <Button label={`Restore removed orders (${removed.length})`} text onClick={() => setRemoved([])} />}
      </div>
    </div>
  </>;
  return embedded ? <section className={`smart-map-route-review${map !== undefined ? " smart-map-route-sidebar" : ""}`} aria-label="Review delivery routes">
    <div className="smart-map-review-actions"><strong>Review delivery routes</strong>{footer}</div>{content}
  </section> : <Dialog header="Review delivery routes" visible modal style={{ width: "min(96vw, 1500px)" }}
    onHide={() => close(null)} footer={footer}>{content}</Dialog>;
}

export function reviewDriverRoutes(context: RouteReviewContext): Promise<OptimizedDriverRoute[] | null> {
  const host = document.createElement("div"); document.body.appendChild(host);
  const root = createRoot(host);
  return new Promise(resolve => {
    root.render(<RoutePreviewDialog {...context} onClose={result => {
      resolve(result);
      window.setTimeout(() => { root.unmount(); host.remove(); }, 0);
    }} />);
  });
}
